import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle, Navigation } from 'lucide-react';
import { officeLocations } from '../data';
import InteractiveMap from '../components/InteractiveMap';

export default function ContactPage() {
  const office = officeLocations[0];
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 4000);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}>
      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050d1a] via-[#0a1628] to-[#0f2240]" />
        <div className="absolute top-20 right-20 w-[500px] h-[500px] bg-[#c9a84c]/5 rounded-full blur-[150px]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="gold-line mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-[#e8e4dc] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
            Contact <span className="text-[#c9a84c]">Us</span>
          </h1>
          <p className="text-lg text-[#e8e4dc]/60 max-w-2xl">
            Get in touch with our team. We're here to help you navigate your legal matters with confidence.
          </p>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <h3 className="text-2xl font-bold text-[#e8e4dc] mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>
                Get in <span className="text-[#c9a84c]">Touch</span>
              </h3>
              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#c9a84c]/10 flex items-center justify-center shrink-0">
                    <MapPin className="w-6 h-6 text-[#c9a84c]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#e8e4dc] mb-1">Office Address</p>
                    <p className="text-sm text-[#e8e4dc]/60">{office.address}</p>
                    <p className="text-sm text-[#e8e4dc]/60">{office.city}, {office.state} {office.zip}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#c9a84c]/10 flex items-center justify-center shrink-0">
                    <Phone className="w-6 h-6 text-[#c9a84c]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#e8e4dc] mb-1">Phone</p>
                    <a href="tel:+12017984100" className="text-sm text-[#c9a84c] hover:text-[#e0cb82] transition-colors">
                      {office.phone}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#c9a84c]/10 flex items-center justify-center shrink-0">
                    <Mail className="w-6 h-6 text-[#c9a84c]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#e8e4dc] mb-1">Email</p>
                    <a href="mailto:info@summitlegal.com" className="text-sm text-[#c9a84c] hover:text-[#e0cb82] transition-colors">
                      info@summitlegal.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#c9a84c]/10 flex items-center justify-center shrink-0">
                    <Clock className="w-6 h-6 text-[#c9a84c]" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-[#e8e4dc] mb-1">Office Hours</p>
                    <p className="text-sm text-[#e8e4dc]/60">{office.hours}</p>
                    <p className="text-sm text-[#e8e4dc]/40">Saturday by appointment</p>
                  </div>
                </div>
              </div>
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${office.lat},${office.lng}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#c9a84c]/10 border border-[#c9a84c]/20 text-sm font-medium text-[#c9a84c] hover:bg-[#c9a84c]/20 transition-all"
              >
                <Navigation className="w-4 h-4" /> Get Directions
              </a>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="p-8 rounded-2xl bg-gradient-to-b from-[#0f2240]/60 to-[#0a1628]/60 border border-[#c9a84c]/15">
                <h3 className="text-xl font-bold text-[#e8e4dc] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Send Us a Message
                </h3>
                {submitted ? (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-12">
                    <CheckCircle className="w-12 h-12 text-[#c9a84c] mx-auto mb-4" />
                    <h4 className="text-lg font-bold text-[#e8e4dc] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Message Sent!</h4>
                    <p className="text-sm text-[#e8e4dc]/50">We'll respond within 24 business hours.</p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-[#c9a84c] font-semibold uppercase tracking-wider mb-2">Full Name</label>
                        <input
                          type="text"
                          required
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          className="w-full px-4 py-3 bg-[#0a1628]/60 border border-[#c9a84c]/15 rounded-xl text-sm text-[#e8e4dc] focus:border-[#c9a84c]/40 focus:outline-none transition-colors placeholder:text-[#e8e4dc]/20"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-[#c9a84c] font-semibold uppercase tracking-wider mb-2">Email</label>
                        <input
                          type="email"
                          required
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          className="w-full px-4 py-3 bg-[#0a1628]/60 border border-[#c9a84c]/15 rounded-xl text-sm text-[#e8e4dc] focus:border-[#c9a84c]/40 focus:outline-none transition-colors placeholder:text-[#e8e4dc]/20"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs text-[#c9a84c] font-semibold uppercase tracking-wider mb-2">Phone</label>
                        <input
                          type="tel"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          className="w-full px-4 py-3 bg-[#0a1628]/60 border border-[#c9a84c]/15 rounded-xl text-sm text-[#e8e4dc] focus:border-[#c9a84c]/40 focus:outline-none transition-colors placeholder:text-[#e8e4dc]/20"
                          placeholder="+1 (201) 555-0123"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-[#c9a84c] font-semibold uppercase tracking-wider mb-2">Subject</label>
                        <input
                          type="text"
                          value={form.subject}
                          onChange={(e) => setForm({ ...form, subject: e.target.value })}
                          className="w-full px-4 py-3 bg-[#0a1628]/60 border border-[#c9a84c]/15 rounded-xl text-sm text-[#e8e4dc] focus:border-[#c9a84c]/40 focus:outline-none transition-colors placeholder:text-[#e8e4dc]/20"
                          placeholder="How can we help?"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-[#c9a84c] font-semibold uppercase tracking-wider mb-2">Message</label>
                      <textarea
                        required
                        rows={5}
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        className="w-full px-4 py-3 bg-[#0a1628]/60 border border-[#c9a84c]/15 rounded-xl text-sm text-[#e8e4dc] focus:border-[#c9a84c]/40 focus:outline-none transition-colors resize-none placeholder:text-[#e8e4dc]/20"
                        placeholder="Tell us about your legal matter..."
                      />
                    </div>
                    <button
                      type="submit"
                      className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-[#c9a84c] to-[#b08e2e] text-[#0a1628] font-semibold rounded-xl hover:shadow-lg hover:shadow-[#c9a84c]/20 transition-all duration-300 hover:scale-[1.02] text-sm"
                    >
                      <Send className="w-4 h-4" /> Send Message
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <div className="gold-line mb-6" />
            <h2 className="text-3xl font-bold text-[#e8e4dc]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Find Our <span className="text-[#c9a84c]">Office</span>
            </h2>
          </div>
          <InteractiveMap />
        </div>
      </section>
    </motion.div>
  );
}
